import { useLayoutEffect } from 'react';

const SITE_ORIGIN = 'https://brianhe.studio';
const DEFAULT_DESCRIPTION =
  'Art, photography, and visual storytelling by Zongheng Brian He. Creating with purpose. Connecting through color.';
const DEFAULT_OG_IMAGE = '/images/home-hero.jpg';

const META = {
  '/': {
    theme: 'home',
    title: 'Zongheng Brian He — Where Color Becomes Care',
    description: DEFAULT_DESCRIPTION,
    image: '/images/home-hero.jpg',
  },
  '/portfolio': {
    theme: 'portfolio',
    title: 'Featured Work — Zongheng Brian He',
    description:
      'Selected paintings, drawings, and mixed media work — exploring identity, choice, and the symbolic textures of everyday life.',
    image: '/images/portfolio-hero.jpg',
  },
  '/club': {
    theme: 'club',
    title: 'Club — Color and Care',
    description:
      'Color and Care is a student-led nonprofit using art to bring warmth and connection to children, especially those with autism.',
    image: '/images/club-hero.jpg',
  },
  '/brand': {
    theme: 'brand',
    title: 'Brand — MADE TO BE SEEN',
    description:
      'A concept-driven fashion brand that turns artwork by children with autism into wearable pieces — visibility through design.',
    image: '/images/brand-hero.jpg',
  },
  '/contact': {
    theme: 'contact',
    title: 'Contact — Zongheng Brian He',
    description: 'Studio inquiries, collaborations, and conversations.',
    image: '/images/contact-hero.jpg',
  },
};

function setMeta(name, content, useProperty = false) {
  if (!content) return;
  const attr = useProperty ? 'property' : 'name';
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setLink(rel, href) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

function setJsonLd(id, data) {
  const old = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (old) old.remove();
  if (!data) return;
  const tag = document.createElement('script');
  tag.type = 'application/ld+json';
  tag.dataset.jsonld = id;
  tag.textContent = JSON.stringify(data);
  document.head.appendChild(tag);
}

function buildJsonLd(pathname, m) {
  const base = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: SITE_ORIGIN + pathname,
    name: m.title,
    description: m.description,
    image: SITE_ORIGIN + m.image,
  };
  if (pathname === '/') {
    return {
      ...base,
      '@type': 'Person',
      name: 'Zongheng Brian He',
      alternateName: 'Brian He',
      jobTitle: 'Artist · Designer · Founder',
      url: SITE_ORIGIN,
      image: SITE_ORIGIN + '/images/avatar.jpg',
      sameAs: [],
      address: { '@type': 'PostalAddress', addressLocality: 'Los Angeles', addressRegion: 'CA' },
    };
  }
  return base;
}

// Title, meta tags and page theme for the URL. Where the page lands is
// useScrollLanding's job.
export function usePageMeta(pathname) {
  // useLayoutEffect runs synchronously after commit, *before* the browser captures
  // the new view-transition snapshot — so the new theme is baked into the target.
  useLayoutEffect(() => {
    let m = META[pathname];
    if (!m) {
      if (pathname.startsWith('/portfolio/')) {
        m = {
          ...META['/portfolio'],
          nav: 'on-light',
        };
      } else {
        m = META['/'];
      }
    }
    document.body.dataset.theme = m.theme;
    if (m.nav) document.body.dataset.navMode = m.nav;
    else delete document.body.dataset.navMode;
    document.title = m.title;

    const url = SITE_ORIGIN + pathname;
    const imageUrl = SITE_ORIGIN + (m.image || DEFAULT_OG_IMAGE);

    setMeta('description', m.description || DEFAULT_DESCRIPTION);
    setLink('canonical', url);

    setMeta('og:type', 'website', true);
    setMeta('og:url', url, true);
    setMeta('og:title', m.title, true);
    setMeta('og:description', m.description || DEFAULT_DESCRIPTION, true);
    setMeta('og:image', imageUrl, true);
    setMeta('og:site_name', 'Zongheng Brian He', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:url', url);
    setMeta('twitter:title', m.title);
    setMeta('twitter:description', m.description || DEFAULT_DESCRIPTION);
    setMeta('twitter:image', imageUrl);

    setJsonLd('page', buildJsonLd(pathname, m));
  }, [pathname]);
}
